/**
 * تنفيذ دورة حياة التنظيم في الوضع المحلي.
 *
 * هذا الملف يقابل `manage_org_entity` و`list_org_archive` في PostgreSQL:
 * نفس التحقق من الصلاحية والنطاق، ونفس منع تجاوز الأب، ونفس التدقيق.
 * لا يمر عبر مستودع الجداول لأن العناصر المحذوفة مخفية عن القراءة العادية،
 * لكنه يظل محميًا بفحص صريح للسياق قبل أي كتابة.
 */

import { AppError } from '@/domain/errors';
import type { Task } from '@/domain/types';
import type { ArchivedOrgRecord, OrgArchiveQuery, OrgLifecycleInput } from '../types';
import type { PolicyContext } from '../policy';
import { getLocalDb } from './db';

interface LifecycleCore {
  policy(): PolicyContext;
  now(): Date;
  rawGet<T extends import('../types').TableName>(
    name: T,
    id: string,
  ): Promise<import('../types').Row<T> | null>;
  rawFind<T extends import('../types').TableName>(
    name: T,
    field: string,
    value: unknown,
  ): Promise<Array<import('../types').Row<T>>>;
  rawAll<T extends import('../types').TableName>(
    name: T,
  ): Promise<Array<import('../types').Row<T>>>;
  rawPut<T extends import('../types').TableName>(
    name: T,
    row: import('../types').Row<T>,
  ): Promise<void>;
  audit(input: {
    companyId: string | null;
    action: string;
    entityType: string;
    entityId: string | null;
    before?: unknown;
    after?: unknown;
  }): Promise<void>;
}

type AvailabilityRow = {
  deletedAt?: string | null;
  status?: string;
  brandId?: string | null;
};

export function localCompanyAvailable(company: AvailabilityRow | null | undefined): boolean {
  return !!company && !company.deletedAt && company.status === 'active';
}

export function localBrandAvailable(
  brand: AvailabilityRow | null | undefined,
  company: AvailabilityRow | null | undefined,
): boolean {
  return (
    !!brand &&
    !brand.deletedAt &&
    brand.status === 'active' &&
    localCompanyAvailable(company)
  );
}

export function localBranchAvailable(
  branch: AvailabilityRow | null | undefined,
  company: AvailabilityRow | null | undefined,
  brand: AvailabilityRow | null | undefined,
): boolean {
  return (
    !!branch &&
    !branch.deletedAt &&
    branch.status === 'active' &&
    localCompanyAvailable(company) &&
    (!branch.brandId || localBrandAvailable(brand, company))
  );
}

function requirePermission(ctx: PolicyContext, permission: string): void {
  if (!ctx.permissions.has(permission as import('@/domain/permissions').PermissionKey)) {
    throw AppError.forbidden('لا تملك الصلاحية المطلوبة لهذا الإجراء');
  }
}

function requireCompany(ctx: PolicyContext): string {
  if (!ctx.companyId) throw AppError.forbidden('لا توجد منشأة نشطة');
  return ctx.companyId;
}

function coversBranch(ctx: PolicyContext, branchId: string): boolean {
  return ctx.scope?.companyWide === true || ctx.scope?.branchIds.has(branchId) === true;
}

function coversBrand(ctx: PolicyContext, brandId: string): boolean {
  if (ctx.scope?.companyWide === true) return true;
  const membership = ctx.access.membership;
  if (!membership) return false;
  const direct = [
    ...membership.roles.flatMap((role) => role.scopes),
    ...membership.grants
      .filter((grant) => grant.revokedAt === null)
      .map((grant) => ({ scopeType: grant.scopeType, targetId: grant.targetId })),
  ];
  return direct.some(
    (scope) =>
      scope.scopeType === 'brand' &&
      (scope.targetId === null || scope.targetId === brandId),
  );
}

async function targetScope(
  core: LifecycleCore,
  membershipId: string,
): Promise<{ branchIds: Set<string>; brandIds: Set<string> }> {
  const membership = await core.rawGet('memberships', membershipId);
  const branchIds = new Set<string>();
  const brandIds = new Set<string>();
  if (membership?.primaryBranchId) branchIds.add(membership.primaryBranchId);
  const placements = (await core.rawFind('membership_placements', 'membershipId', membershipId)).filter(
    (placement) => !placement.deletedAt,
  );
  for (const placement of placements) {
    if (placement.scopeType === 'branch') branchIds.add(placement.scopeId);
    else brandIds.add(placement.scopeId);
  }
  for (const branchId of branchIds) {
    const branch = await core.rawGet('branches', branchId);
    if (branch?.brandId) brandIds.add(branch.brandId);
  }
  return { branchIds, brandIds };
}

async function coversMembership(
  core: LifecycleCore,
  ctx: PolicyContext,
  membershipId: string,
): Promise<boolean> {
  if (ctx.scope?.companyWide) return true;
  if (ctx.scope?.membershipIds.has(membershipId)) return true;
  const target = await targetScope(core, membershipId);
  if ([...target.branchIds].some((id) => ctx.scope?.branchIds.has(id))) return true;
  if ([...target.brandIds].some((id) => ctx.scope?.brandIds.has(id))) return true;
  return false;
}

async function coversDeletedMembershipScope(
  core: LifecycleCore,
  ctx: PolicyContext,
  membership: import('../types').Row<'memberships'>,
): Promise<boolean> {
  if (ctx.isPlatformOwner || ctx.scope?.companyWide) return true;
  if (membership.deletedScopeType === 'company') return false;
  if (membership.deletedScopeType === 'branch') {
    return membership.deletedScopeId ? coversBranch(ctx, membership.deletedScopeId) : false;
  }
  if (membership.deletedScopeType === 'brand') {
    return membership.deletedScopeId ? coversBrand(ctx, membership.deletedScopeId) : false;
  }
  // السجلات القديمة التي لا تحفظ نطاق الحذف لا تسمح بتجاوز التحقق الحالي.
  return coversMembership(core, ctx, membership.id);
}

async function audit(
  core: LifecycleCore,
  ctx: PolicyContext,
  companyId: string | null,
  action: string,
  entityType: string,
  entityId: string,
  before: unknown,
  after: unknown,
): Promise<void> {
  await core.audit({
    companyId,
    action,
    entityType,
    entityId,
    before,
    after: { ...(after as Record<string, unknown> | null), actorMembershipId: ctx.membershipId },
  });
}

async function purgeBlockers(
  core: LifecycleCore,
  entityType: string,
  entityId: string,
  companyId: string | null,
): Promise<string[]> {
  const blockers: string[] = [];

  if (entityType === 'company') {
    if ((await core.rawFind('tasks', 'companyId', entityId)).length) blockers.push('مهام');
    if ((await core.rawFind('task_templates', 'companyId', entityId)).length)
      blockers.push('قوالب مهام');
    if ((await core.rawFind('attachments', 'companyId', entityId)).length) blockers.push('مرفقات');
    if ((await core.rawFind('attachment_deletions', 'companyId', entityId)).length)
      blockers.push('سجلات احتفاظ');
  } else if (entityType === 'brand') {
    if ((await core.rawFind('tasks', 'brandId', entityId)).length) blockers.push('مهام مرتبطة بالعلامة');
    if ((await core.rawFind('task_templates', 'brandId', entityId)).length)
      blockers.push('قوالب مرتبطة بالعلامة');
  } else if (entityType === 'branch') {
    if ((await core.rawFind('tasks', 'branchId', entityId)).length) blockers.push('مهام مرتبطة بالفرع');
    if ((await core.rawFind('task_templates', 'branchId', entityId)).length)
      blockers.push('قوالب مرتبطة بالفرع');
    if ((await core.rawFind('memberships', 'primaryBranchId', entityId)).length)
      blockers.push('موظفون مرتبطون بالفرع');
  } else if (entityType === 'membership') {
    const companyTasks = companyId ? await core.rawFind('tasks', 'companyId', companyId) : [];
    const related = companyTasks.filter(
      (task) =>
        task.createdByMembershipId === entityId ||
        task.approverMembershipId === entityId ||
        (task as Task & { closedByMembershipId?: string | null }).closedByMembershipId === entityId,
    );
    if (related.length) blockers.push('مهام منشأة أو معتمدة بواسطة الموظف');
    if ((await core.rawFind('task_assignees', 'membershipId', entityId)).length)
      blockers.push('إسنادات مهام');
    const taskIds = new Set(companyTasks.map((task) => task.id));
    const history = (await core.rawFind('task_status_history', 'actorMembershipId', entityId)).filter(
      (row) => taskIds.has(row.taskId),
    );
    if (history.length) blockers.push('سجل حالات المهام');
  }
  return blockers;
}

export async function manageOrgEntityLocal(
  core: LifecycleCore,
  input: OrgLifecycleInput,
): Promise<void> {
  const ctx = core.policy();
  if (!ctx.userId) throw AppError.unauthenticated();
  const now = core.now().toISOString();
  const reason = input.reason?.trim() || null;
  if (reason && reason.length > 500) throw AppError.validation('سبب الحذف أطول من الحد المسموح');
  const action = input.action;
  const scopeType = input.scopeType ?? 'company';

  if (input.entityType === 'company') {
    if (!ctx.isPlatformOwner) throw AppError.forbidden('إدارة المنشآت متاحة لمالك المنصة فقط');
    const company = await core.rawGet('companies', input.entityId);
    if (!company) throw AppError.notFound('المنشأة غير موجودة');

    if (action === 'suspend') {
      if (company.deletedAt) throw AppError.validation('لا يمكن إيقاف منشأة محذوفة. استعدها أولًا.');
      await core.rawPut('companies', {
        ...company,
        status: 'disabled',
        updatedAt: now,
      });
      await audit(core, ctx, company.id, 'company.suspended', 'company', company.id,
        { status: company.status }, { status: 'disabled', reason });
    } else if (action === 'activate') {
      if (company.deletedAt) throw AppError.validation('لا يمكن تفعيل منشأة محذوفة. استعدها أولًا.');
      await core.rawPut('companies', { ...company, status: 'active', updatedAt: now });
      await audit(core, ctx, company.id, 'company.activated', 'company', company.id,
        { status: company.status }, { status: 'active', reason });
    } else if (action === 'delete') {
      if (company.deletedAt) throw AppError.validation('المنشأة محذوفة مسبقًا');
      await core.rawPut('companies', {
        ...company,
        deletedAt: now,
        deletedBy: ctx.userId,
        deletedReason: reason,
        updatedAt: now,
      });
      await audit(core, ctx, company.id, 'company.deleted', 'company', company.id,
        { status: company.status }, { deletedAt: now, reason });
    } else if (action === 'restore') {
      if (!company.deletedAt) throw AppError.validation('المنشأة غير محذوفة');
      await core.rawPut('companies', {
        ...company,
        deletedAt: null,
        deletedBy: null,
        deletedReason: null,
        updatedAt: now,
      });
      await audit(core, ctx, company.id, 'company.restored', 'company', company.id,
        { deletedAt: company.deletedAt }, { deletedAt: null, status: company.status });
    } else if (action === 'purge') {
      if (!company.deletedAt) throw AppError.validation('لا يمكن الحذف النهائي قبل الحذف المنطقي');
      if ((input.confirmation ?? '') !== company.name) {
        throw AppError.validation('اكتب اسم المنشأة حرفيًا لتأكيد الحذف النهائي');
      }
      const blockers = await purgeBlockers(core, 'company', company.id, company.id);
      if (blockers.length) {
        throw AppError.conflict(`لا يمكن الحذف النهائي لوجود تاريخ تشغيلي: ${blockers.join('، ')}`);
      }
      await audit(core, ctx, company.id, 'company.purged', 'company', company.id,
        { name: company.name }, null);
      const db = await getLocalDb();
      const scopedSettingIds = new Set<string>([company.id]);
      for (const brand of await core.rawFind('brands', 'companyId', company.id)) {
        scopedSettingIds.add(brand.id);
      }
      for (const branch of await core.rawFind('branches', 'companyId', company.id)) {
        scopedSettingIds.add(branch.id);
      }
      for (const store of [
        'membership_placements',
        'membership_roles',
        'membership_access_grants',
        'supervision_relations',
        'memberships',
        'roles',
        'task_categories',
        'brand_branch_links',
        'brands',
        'branches',
        'company_modules',
        'notifications',
      ] as const) {
        const rows = await core.rawAll(store);
        for (const row of rows) {
          if ((row as unknown as Record<string, unknown>)['companyId'] === company.id) {
            await db.delete(store, row.id);
          }
        }
      }
      const settings = await core.rawAll('settings');
      for (const setting of settings) {
        if (
          (setting.scopeType === 'company' ||
            setting.scopeType === 'brand' ||
            setting.scopeType === 'branch') &&
          scopedSettingIds.has(setting.scopeId)
        ) {
          await db.delete('settings', setting.id);
        }
      }
      await db.delete('companies', company.id);
    } else {
      throw AppError.validation('إجراء غير معروف');
    }
    return;
  }

  const companyId = requireCompany(ctx);

  if (input.entityType === 'brand') {
    const brand = await core.rawGet('brands', input.entityId);
    if (!brand || brand.companyId !== companyId) throw AppError.notFound('العلامة غير موجودة');
    const company = await core.rawGet('companies', companyId);
    if (!localCompanyAvailable(company)) {
      throw AppError.forbidden('المنشأة غير متاحة. استعد المنشأة أولًا.');
    }
    requirePermission(ctx, 'brand.manage');
    if (action === 'purge') requirePermission(ctx, 'company.manage');

    if (action === 'suspend') {
      if (brand.deletedAt) throw AppError.validation('لا يمكن إيقاف علامة محذوفة');
      await core.rawPut('brands', { ...brand, status: 'disabled', updatedAt: now });
      await audit(core, ctx, companyId, 'brand.suspended', 'brand', brand.id,
        { status: brand.status }, { status: 'disabled', reason });
    } else if (action === 'activate') {
      if (brand.deletedAt) throw AppError.validation('لا يمكن تفعيل علامة محذوفة');
      await core.rawPut('brands', { ...brand, status: 'active', updatedAt: now });
      await audit(core, ctx, companyId, 'brand.activated', 'brand', brand.id,
        { status: brand.status }, { status: 'active', reason });
    } else if (action === 'delete') {
      if (brand.deletedAt) throw AppError.validation('العلامة محذوفة مسبقًا');
      await core.rawPut('brands', {
        ...brand,
        deletedAt: now,
        deletedBy: ctx.userId,
        deletedReason: reason,
        updatedAt: now,
      });
      await audit(core, ctx, companyId, 'brand.deleted', 'brand', brand.id,
        { status: brand.status }, { deletedAt: now, reason });
    } else if (action === 'restore') {
      if (!brand.deletedAt) throw AppError.validation('العلامة غير محذوفة');
      await core.rawPut('brands', {
        ...brand,
        deletedAt: null,
        deletedBy: null,
        deletedReason: null,
        updatedAt: now,
      });
      await audit(core, ctx, companyId, 'brand.restored', 'brand', brand.id,
        { deletedAt: brand.deletedAt }, { deletedAt: null });
    } else if (action === 'purge') {
      if (!brand.deletedAt) throw AppError.validation('لا يمكن الحذف النهائي قبل الحذف المنطقي');
      if ((input.confirmation ?? '') !== brand.name) {
        throw AppError.validation('اكتب اسم العلامة حرفيًا لتأكيد الحذف النهائي');
      }
      const blockers = await purgeBlockers(core, 'brand', brand.id, companyId);
      if (blockers.length) {
        throw AppError.conflict(`لا يمكن الحذف النهائي لوجود تاريخ تشغيلي: ${blockers.join('، ')}`);
      }
      await audit(core, ctx, companyId, 'brand.purged', 'brand', brand.id,
        { name: brand.name }, null);
      const db = await getLocalDb();
      for (const placement of await core.rawFind('membership_placements', 'scopeId', brand.id)) {
        if (placement.scopeType === 'brand') await db.delete('membership_placements', placement.id);
      }
      for (const link of await core.rawFind('brand_branch_links', 'brandId', brand.id)) {
        await db.delete('brand_branch_links', link.id);
      }
      for (const branch of await core.rawFind('branches', 'brandId', brand.id)) {
        await core.rawPut('branches', { ...branch, brandId: null });
      }
      await db.delete('brands', brand.id);
    } else {
      throw AppError.validation('إجراء غير معروف');
    }
    return;
  }

  if (input.entityType === 'branch') {
    const branch = await core.rawGet('branches', input.entityId);
    if (!branch || branch.companyId !== companyId) throw AppError.notFound('الفرع غير موجود');
    const company = await core.rawGet('companies', companyId);
    if (!localCompanyAvailable(company)) {
      throw AppError.forbidden('المنشأة غير متاحة. استعد المنشأة أولًا.');
    }
    requirePermission(ctx, 'branch.manage');
    if (!coversBranch(ctx, branch.id)) throw AppError.forbidden('الفرع خارج نطاقك');
    if (action === 'purge') requirePermission(ctx, 'company.manage');

    if (action === 'suspend') {
      if (branch.deletedAt) throw AppError.validation('لا يمكن إيقاف فرع محذوف');
      await core.rawPut('branches', { ...branch, status: 'disabled', updatedAt: now });
      await audit(core, ctx, companyId, 'branch.suspended', 'branch', branch.id,
        { status: branch.status }, { status: 'disabled', reason });
    } else if (action === 'activate') {
      if (branch.deletedAt) throw AppError.validation('لا يمكن تفعيل فرع محذوف');
      const brand = branch.brandId ? await core.rawGet('brands', branch.brandId) : null;
      if (branch.brandId && (!brand || brand.deletedAt || brand.status !== 'active')) {
        throw AppError.forbidden('لا يمكن تفعيل الفرع قبل تفعيل علامته');
      }
      await core.rawPut('branches', { ...branch, status: 'active', updatedAt: now });
      await audit(core, ctx, companyId, 'branch.activated', 'branch', branch.id,
        { status: branch.status }, { status: 'active', reason });
    } else if (action === 'delete') {
      if (branch.deletedAt) throw AppError.validation('الفرع محذوف مسبقًا');
      await core.rawPut('branches', {
        ...branch,
        deletedAt: now,
        deletedBy: ctx.userId,
        deletedReason: reason,
        updatedAt: now,
      });
      await audit(core, ctx, companyId, 'branch.deleted', 'branch', branch.id,
        { status: branch.status }, { deletedAt: now, reason });
    } else if (action === 'restore') {
      if (!branch.deletedAt) throw AppError.validation('الفرع غير محذوف');
      const brand = branch.brandId ? await core.rawGet('brands', branch.brandId) : null;
      if (branch.brandId && (!brand || brand.deletedAt || brand.status !== 'active')) {
        throw AppError.forbidden('لا يمكن استعادة الفرع قبل استعادة علامته وتفعيلها');
      }
      await core.rawPut('branches', {
        ...branch,
        deletedAt: null,
        deletedBy: null,
        deletedReason: null,
        updatedAt: now,
      });
      await audit(core, ctx, companyId, 'branch.restored', 'branch', branch.id,
        { deletedAt: branch.deletedAt }, { deletedAt: null });
    } else if (action === 'purge') {
      if (!branch.deletedAt) throw AppError.validation('لا يمكن الحذف النهائي قبل الحذف المنطقي');
      if ((input.confirmation ?? '') !== branch.name) {
        throw AppError.validation('اكتب اسم الفرع حرفيًا لتأكيد الحذف النهائي');
      }
      const blockers = await purgeBlockers(core, 'branch', branch.id, companyId);
      if (blockers.length) {
        throw AppError.conflict(`لا يمكن الحذف النهائي لوجود تاريخ تشغيلي: ${blockers.join('، ')}`);
      }
      await audit(core, ctx, companyId, 'branch.purged', 'branch', branch.id,
        { name: branch.name }, null);
      const db = await getLocalDb();
      for (const placement of await core.rawFind('membership_placements', 'scopeId', branch.id)) {
        if (placement.scopeType === 'branch') await db.delete('membership_placements', placement.id);
      }
      for (const membership of await core.rawFind('memberships', 'primaryBranchId', branch.id)) {
        await core.rawPut('memberships', { ...membership, primaryBranchId: null });
      }
      for (const link of await core.rawFind('brand_branch_links', 'branchId', branch.id)) {
        await db.delete('brand_branch_links', link.id);
      }
      await db.delete('branches', branch.id);
    } else {
      throw AppError.validation('إجراء غير معروف');
    }
    return;
  }

  if (input.entityType === 'membership') {
    const membership = await core.rawGet('memberships', input.entityId);
    if (!membership || membership.companyId !== companyId) throw AppError.notFound('العضوية غير موجودة');
    requirePermission(ctx, 'membership.manage');
    const withinScope = action === 'restore' && membership.deletedAt
      ? await coversDeletedMembershipScope(core, ctx, membership)
      : await coversMembership(core, ctx, membership.id);
    if (!withinScope) {
      throw AppError.forbidden('الموظف خارج نطاقك');
    }
    if (membership.id === ctx.membershipId && ['delete', 'suspend', 'purge'].includes(action)) {
      throw AppError.forbidden('لا يمكنك حذف أو إيقاف عضويتك الحالية');
    }
    if (action === 'purge') requirePermission(ctx, 'company.manage');

    if (action === 'suspend') {
      if (membership.deletedAt) throw AppError.validation('لا يمكن إيقاف موظف محذوف');
      await core.rawPut('memberships', { ...membership, status: 'suspended', updatedAt: now });
      await audit(core, ctx, companyId, 'membership.suspended', 'membership', membership.id,
        { status: membership.status }, { status: 'suspended', reason });
    } else if (action === 'activate') {
      if (membership.deletedAt) throw AppError.validation('لا يمكن تفعيل موظف محذوف');
      await core.rawPut('memberships', { ...membership, status: 'active', updatedAt: now });
      await audit(core, ctx, companyId, 'membership.activated', 'membership', membership.id,
        { status: membership.status }, { status: 'active', reason });
    } else if (action === 'delete') {
      if (membership.deletedAt) throw AppError.validation('الموظف محذوف مسبقًا');
      const membershipScopeType =
        scopeType === 'branch' || scopeType === 'brand' ? scopeType : 'company';
      if (scopeType === 'branch') {
        if (!input.scopeId) throw AppError.validation('نطاق الفرع مطلوب');
        if (!(await coversBranch(ctx, input.scopeId))) throw AppError.forbidden('الفرع خارج نطاقك');
        const linked = (await core.rawFind('membership_placements', 'membershipId', membership.id)).some(
          (placement) =>
            placement.scopeType === 'branch' &&
            placement.scopeId === input.scopeId &&
            !placement.deletedAt,
        );
        if (!linked) throw AppError.validation('الموظف غير مرتبط بهذا الفرع');
      } else if (scopeType === 'brand') {
        if (!input.scopeId) throw AppError.validation('نطاق العلامة مطلوب');
        if (!coversBrand(ctx, input.scopeId)) throw AppError.forbidden('العلامة خارج نطاقك');
        const linked = (await core.rawFind('membership_placements', 'membershipId', membership.id)).some(
          (placement) =>
            placement.scopeType === 'brand' &&
            placement.scopeId === input.scopeId &&
            !placement.deletedAt,
        );
        if (!linked) throw AppError.validation('الموظف غير مرتبط بهذه العلامة');
      } else if (!ctx.scope?.companyWide) {
        throw AppError.forbidden('حذف الموظف من المنشأة يتطلب نطاقًا شاملًا');
      }
      await core.rawPut('memberships', {
        ...membership,
        deletedAt: now,
        deletedBy: ctx.userId,
        deletedReason: reason,
        deletedScopeType: membershipScopeType,
        deletedScopeId: input.scopeId ?? null,
        updatedAt: now,
      });
      await audit(core, ctx, companyId, 'membership.deleted', 'membership', membership.id,
        { status: membership.status },
        { deletedAt: now, reason, scopeType: membershipScopeType, scopeId: input.scopeId });
    } else if (action === 'restore') {
      if (!membership.deletedAt) throw AppError.validation('الموظف غير محذوف');
      await core.rawPut('memberships', {
        ...membership,
        deletedAt: null,
        deletedBy: null,
        deletedReason: null,
        deletedScopeType: null,
        deletedScopeId: null,
        updatedAt: now,
      });
      await audit(core, ctx, companyId, 'membership.restored', 'membership', membership.id,
        { deletedAt: membership.deletedAt }, { deletedAt: null });
    } else if (action === 'purge') {
      if (!membership.deletedAt) throw AppError.validation('لا يمكن الحذف النهائي قبل الحذف المنطقي');
      const profile = await core.rawGet('profiles', membership.userId);
      if ((input.confirmation ?? '') !== (profile?.fullName ?? '')) {
        throw AppError.validation('اكتب اسم الموظف حرفيًا لتأكيد الحذف النهائي');
      }
      const blockers = await purgeBlockers(core, 'membership', membership.id, companyId);
      if (blockers.length) {
        throw AppError.conflict(`لا يمكن الحذف النهائي لوجود تاريخ تشغيلي: ${blockers.join('، ')}`);
      }
      await audit(core, ctx, companyId, 'membership.purged', 'membership', membership.id,
        { name: profile?.fullName }, null);
      const db = await getLocalDb();
      for (const placement of await core.rawFind('membership_placements', 'membershipId', membership.id)) {
        await db.delete('membership_placements', placement.id);
      }
      for (const role of await core.rawFind('membership_roles', 'membershipId', membership.id)) {
        await db.delete('membership_roles', role.id);
      }
      for (const grant of await core.rawFind('membership_access_grants', 'membershipId', membership.id)) {
        await db.delete('membership_access_grants', grant.id);
      }
      for (const sup of (await core.rawAll('supervision_relations')).filter(
        (row) =>
          row.supervisorMembershipId === membership.id ||
          row.subordinateMembershipId === membership.id,
      )) {
        await db.delete('supervision_relations', sup.id);
      }
      await db.delete('memberships', membership.id);
    } else {
      throw AppError.validation('إجراء غير معروف');
    }
    return;
  }

  if (input.entityType === 'placement') {
    const placement = await core.rawGet('membership_placements', input.entityId);
    if (!placement || placement.companyId !== companyId) {
      throw AppError.notFound('ارتباط الموظف غير موجود');
    }
    requirePermission(ctx, 'membership.manage');
    if (!(await coversMembership(core, ctx, placement.membershipId))) {
      throw AppError.forbidden('ارتباط الموظف خارج نطاقك');
    }
    if (action === 'purge') requirePermission(ctx, 'company.manage');
    const targetName =
      placement.scopeType === 'branch'
        ? (await core.rawGet('branches', placement.scopeId))?.name ?? 'فرع'
        : (await core.rawGet('brands', placement.scopeId))?.name ?? 'علامة';

    if (action === 'delete') {
      if (placement.deletedAt) throw AppError.validation('الارتباط محذوف مسبقًا');
      await core.rawPut('membership_placements', {
        ...placement,
        deletedAt: now,
        deletedBy: ctx.userId,
        deletedReason: reason,
      });
      if (placement.scopeType === 'branch') {
        const membership = await core.rawGet('memberships', placement.membershipId);
        if (membership?.primaryBranchId === placement.scopeId) {
          await core.rawPut('memberships', { ...membership, primaryBranchId: null, updatedAt: now });
        }
      }
      await audit(core, ctx, companyId, 'membership.link_deleted', 'membership_placement', placement.id,
        { scopeType: placement.scopeType, scopeId: placement.scopeId, name: targetName },
        { deletedAt: now, reason });
    } else if (action === 'restore') {
      if (!placement.deletedAt) throw AppError.validation('الارتباط غير محذوف');
      if (placement.scopeType === 'branch') {
        const branch = await core.rawGet('branches', placement.scopeId);
        const company = await core.rawGet('companies', companyId);
        const brand = branch?.brandId ? await core.rawGet('brands', branch.brandId) : null;
        if (!branch || !localBranchAvailable(branch, company, brand)) {
          throw AppError.forbidden('لا يمكن استعادة الارتباط قبل استعادة الفرع وتفعيله');
        }
      } else {
        const brand = await core.rawGet('brands', placement.scopeId);
        const company = await core.rawGet('companies', companyId);
        if (!localBrandAvailable(brand, company)) {
          throw AppError.forbidden('لا يمكن استعادة الارتباط قبل استعادة العلامة وتفعيلها');
        }
      }
      await core.rawPut('membership_placements', {
        ...placement,
        deletedAt: null,
        deletedBy: null,
        deletedReason: null,
      });
      if (placement.scopeType === 'branch') {
        const membership = await core.rawGet('memberships', placement.membershipId);
        if (membership && !membership.primaryBranchId) {
          await core.rawPut('memberships', {
            ...membership,
            primaryBranchId: placement.scopeId,
            updatedAt: now,
          });
        }
      }
      await audit(core, ctx, companyId, 'membership.link_restored', 'membership_placement', placement.id,
        { scopeType: placement.scopeType, scopeId: placement.scopeId, name: targetName },
        { deletedAt: null });
    } else if (action === 'purge') {
      if (!placement.deletedAt) throw AppError.validation('لا يمكن الحذف النهائي قبل الحذف المنطقي');
      await audit(core, ctx, companyId, 'membership.link_purged', 'membership_placement', placement.id,
        { scopeType: placement.scopeType, scopeId: placement.scopeId, name: targetName }, null);
      const db = await getLocalDb();
      await db.delete('membership_placements', placement.id);
    } else {
      throw AppError.validation('الإيقاف والتفعيل لا ينطبقان على ارتباط الموظف');
    }
    return;
  }

  throw AppError.validation('نوع عنصر غير معروف');
}

export async function listOrgArchiveLocal(
  core: LifecycleCore,
  input: OrgArchiveQuery,
): Promise<ArchivedOrgRecord[]> {
  const ctx = core.policy();
  if (!ctx.userId) throw AppError.unauthenticated();
  const scopeType = input.scopeType;
  const scopeId = input.scopeId ?? null;
  const companyId =
    scopeType === 'company'
      ? scopeId
      : scopeType === 'brand'
        ? (await core.rawGet('brands', scopeId ?? ''))?.companyId ?? null
        : (await core.rawGet('branches', scopeId ?? ''))?.companyId ?? null;

  const record = (values: Partial<ArchivedOrgRecord> & {
    entityType: ArchivedOrgRecord['entityType'];
    id: string;
    name: string;
    deletedAt: string;
  }): ArchivedOrgRecord => ({
    entityType: values.entityType,
    id: values.id,
    companyId: values.companyId ?? companyId,
    name: values.name,
    subtitle: values.subtitle ?? null,
    status: values.status ?? 'deleted',
    deletedAt: values.deletedAt,
    deletedBy: values.deletedBy ?? null,
    deletedReason: values.deletedReason ?? null,
    scopeType: values.scopeType ?? null,
    scopeId: values.scopeId ?? null,
    membershipId: values.membershipId ?? null,
    userId: values.userId ?? null,
    email: values.email ?? null,
    branchId: values.branchId ?? null,
    brandId: values.brandId ?? null,
  });

  if (input.entityType === 'company') {
    if (!ctx.isPlatformOwner) throw AppError.forbidden('محذوفات المنشآت متاحة لمالك المنصة فقط');
    return (await core.rawAll('companies'))
      .filter((row) => !!row.deletedAt)
      .sort((a, b) => String(b.deletedAt).localeCompare(String(a.deletedAt)))
      .map((row) =>
        record({
          entityType: 'company',
          id: row.id,
          companyId: row.id,
          name: row.name,
          subtitle: row.slug,
          status: row.status,
          deletedAt: String(row.deletedAt),
          deletedBy: row.deletedBy ?? null,
          deletedReason: row.deletedReason ?? null,
          scopeType: 'company',
          scopeId: row.id,
        }),
      );
  }

  if (!companyId) throw AppError.notFound('نطاق المحذوفات غير موجود');

  if (input.entityType === 'brand') {
    const companyWide = ctx.scope?.companyWide === true;
    const canSee = ctx.isPlatformOwner
      ? true
      : ctx.permissions.has('brand.manage') || ctx.permissions.has('company.manage');
    if (!canSee) throw AppError.forbidden('لا تملك صلاحية عرض محذوفات العلامات');
    if (!ctx.isPlatformOwner && scopeType === 'company' && !companyWide) {
      throw AppError.forbidden('عرض محذوفات كل العلامات يتطلب نطاقًا شاملًا');
    }
    if (!ctx.isPlatformOwner && scopeType === 'brand' && !coversBrand(ctx, scopeId ?? '')) {
      throw AppError.forbidden('العلامة خارج نطاقك');
    }
    return (await core.rawFind('brands', 'companyId', companyId))
      .filter(
        (row) =>
          !!row.deletedAt &&
          (scopeType === 'company' || row.id === scopeId || ctx.isPlatformOwner),
      )
      .sort((a, b) => String(b.deletedAt).localeCompare(String(a.deletedAt)))
      .map((row) =>
        record({
          entityType: 'brand',
          id: row.id,
          name: row.name,
          subtitle: row.code,
          status: row.status,
          deletedAt: String(row.deletedAt),
          deletedBy: row.deletedBy ?? null,
          deletedReason: row.deletedReason ?? null,
          scopeType: 'brand',
          scopeId: row.id,
          brandId: row.id,
        }),
      );
  }

  if (input.entityType === 'branch') {
    if (!ctx.isPlatformOwner) {
      if (scopeType === 'company') {
        if (!ctx.permissions.has('branch.manage') && !ctx.permissions.has('company.manage')) {
          throw AppError.forbidden('لا تملك صلاحية عرض محذوفات الفروع');
        }
      } else if (scopeType === 'brand') {
        if (!coversBrand(ctx, scopeId ?? '')) throw AppError.forbidden('العلامة خارج نطاقك');
      } else if (!coversBranch(ctx, scopeId ?? '')) {
        throw AppError.forbidden('الفرع خارج نطاقك');
      }
    }
    const links = await core.rawFind('brand_branch_links', 'companyId', companyId);
    return (await core.rawFind('branches', 'companyId', companyId))
      .filter((row) => {
        if (!row.deletedAt) return false;
        if (scopeType === 'company') return true;
        if (scopeType === 'branch') return row.id === scopeId;
        return (
          row.brandId === scopeId ||
          links.some((link) => link.branchId === row.id && link.brandId === scopeId)
        );
      })
      .sort((a, b) => String(b.deletedAt).localeCompare(String(a.deletedAt)))
      .map((row) =>
        record({
          entityType: 'branch',
          id: row.id,
          name: row.name,
          subtitle: row.code,
          status: row.status,
          deletedAt: String(row.deletedAt),
          deletedBy: row.deletedBy ?? null,
          deletedReason: row.deletedReason ?? null,
          scopeType: 'branch',
          scopeId: row.id,
          branchId: row.id,
          brandId: row.brandId ?? null,
        }),
      );
  }

  const profiles = new Map((await core.rawAll('profiles')).map((row) => [row.id, row]));
  const memberships = new Map((await core.rawAll('memberships')).map((row) => [row.id, row]));
  const placements = await core.rawFind('membership_placements', 'companyId', companyId);
  const companyBranches = await core.rawFind('branches', 'companyId', companyId);
  const companyLinks = await core.rawFind('brand_branch_links', 'companyId', companyId);
  const brandBranchIds = new Set(
    companyBranches
      .filter(
        (branch) =>
          branch.brandId === scopeId ||
          companyLinks.some((link) => link.branchId === branch.id && link.brandId === scopeId),
      )
      .map((branch) => branch.id),
  );

  if (input.entityType === 'membership') {
    if (!ctx.isPlatformOwner) {
      const allowed =
        ctx.permissions.has('user.view') ||
        ctx.permissions.has('membership.manage') ||
        ctx.permissions.has('company.manage');
      if (!allowed) throw AppError.forbidden('لا تملك صلاحية عرض محذوفات الموظفين');
      if (scopeType === 'company' && !ctx.scope?.companyWide) {
        throw AppError.forbidden('عرض محذوفات كل الموظفين يتطلب نطاقًا شاملًا');
      }
      if (scopeType === 'brand' && !coversBrand(ctx, scopeId ?? '') && !ctx.scope?.companyWide) {
        throw AppError.forbidden('العلامة خارج نطاقك');
      }
      if (scopeType === 'branch' && !coversBranch(ctx, scopeId ?? '')) {
        throw AppError.forbidden('الفرع خارج نطاقك');
      }
    }

    return [...memberships.values()]
      .filter((row) => row.companyId === companyId && !!row.deletedAt)
      .filter((row) => {
        if (ctx.isPlatformOwner || ctx.scope?.companyWide) return true;
        if (scopeType === 'branch') {
          return (
            row.primaryBranchId === scopeId ||
            placements.some(
              (placement) =>
                placement.membershipId === row.id &&
                placement.scopeType === 'branch' &&
                placement.scopeId === scopeId,
            )
          );
        }
        if (scopeType === 'brand') {
          return (
            placements.some(
              (placement) =>
                placement.membershipId === row.id &&
                placement.scopeType === 'brand' &&
                placement.scopeId === scopeId,
            ) ||
            (row.primaryBranchId !== null && brandBranchIds.has(row.primaryBranchId)) ||
            placements.some(
              (placement) =>
                placement.membershipId === row.id &&
                placement.scopeType === 'branch' &&
                brandBranchIds.has(placement.scopeId),
            )
          );
        }
        return false;
      })
      .sort((a, b) => String(b.deletedAt).localeCompare(String(a.deletedAt)))
      .map((row) => {
        const profile = profiles.get(row.userId);
        return record({
          entityType: 'membership',
          id: row.id,
          name: profile?.fullName ?? row.userId,
          subtitle: row.jobTitle ?? profile?.email ?? null,
          status: row.status,
          deletedAt: String(row.deletedAt),
          deletedBy: row.deletedBy ?? null,
          deletedReason: row.deletedReason ?? null,
          scopeType: row.deletedScopeType ?? null,
          scopeId: row.deletedScopeId ?? null,
          membershipId: row.id,
          userId: row.userId,
          email: profile?.email ?? null,
          branchId: row.primaryBranchId ?? null,
          brandId: null,
        });
      });
  }

  if (input.entityType === 'placement') {
    if (!ctx.isPlatformOwner) {
      const allowed =
        ctx.permissions.has('user.view') ||
        ctx.permissions.has('membership.manage') ||
        ctx.permissions.has('company.manage');
      if (!allowed) throw AppError.forbidden('لا تملك صلاحية عرض ارتباطات الموظفين');
      if (scopeType === 'company' && !ctx.scope?.companyWide) {
        throw AppError.forbidden('عرض كل ارتباطات الموظفين يتطلب نطاقًا شاملًا');
      }
      if (scopeType === 'brand' && !coversBrand(ctx, scopeId ?? '') && !ctx.scope?.companyWide) {
        throw AppError.forbidden('العلامة خارج نطاقك');
      }
      if (scopeType === 'branch' && !coversBranch(ctx, scopeId ?? '')) {
        throw AppError.forbidden('الفرع خارج نطاقك');
      }
    }
    return placements
      .filter((row) => !!row.deletedAt)
      .filter((row) => {
        if (scopeType === 'company') return true;
        if (scopeType === 'branch') return row.scopeType === 'branch' && row.scopeId === scopeId;
        if (scopeType === 'brand') {
          return (
            (row.scopeType === 'brand' && row.scopeId === scopeId) ||
            (row.scopeType === 'branch' && brandBranchIds.has(row.scopeId))
          );
        }
        return false;
      })
      .sort((a, b) => String(b.deletedAt).localeCompare(String(a.deletedAt)))
      .map((row) => {
        const membership = memberships.get(row.membershipId);
        const profile = membership ? profiles.get(membership.userId) : null;
        return record({
          entityType: 'placement',
          id: row.id,
          name: profile?.fullName ?? 'ارتباط موظف',
          subtitle: membership?.jobTitle ?? profile?.email ?? null,
          status: 'deleted',
          deletedAt: String(row.deletedAt),
          deletedBy: row.deletedBy ?? null,
          deletedReason: row.deletedReason ?? null,
          scopeType: row.scopeType as 'branch' | 'brand',
          scopeId: row.scopeId,
          membershipId: row.membershipId,
          userId: membership?.userId ?? null,
          email: profile?.email ?? null,
          branchId: row.scopeType === 'branch' ? row.scopeId : membership?.primaryBranchId ?? null,
          brandId: row.scopeType === 'brand' ? row.scopeId : null,
        });
      });
  }

  throw AppError.validation('نوع عنصر غير معروف');
}
